import dayjs from 'dayjs';
import { ApolloCache, ApolloError } from '@apollo/client';

import { describeRateLimitRejection, getRateLimitRejection } from '../../../common/rateLimit';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES, PageSize } from '../contacts/contactList';

/**
 * Match review (card 23, on card 07's API) as plain data and pure functions,
 * tested without rendering: the screen's state and URL, the words for each
 * rule and decision, which values agree between the two sides, and what a
 * refused review means.
 *
 * The queue holds the NationBuilder records the match ladder could not decide
 * alone (ilbp-backend docs/CARD_07_FUZZY_REVIEW_QUEUE.md): every name-and-town
 * match (rule 6), a link nothing else corroborates, and a rule that found
 * several accounts. Nothing links until an admin approves.
 *
 * Card 28: approving also merges the two contacts, the NationBuilder record's
 * into the member's, and reopening undoes it (ilbp-backend
 * docs/CARD_28_MERGE_CONTACTS.md).
 */

/* ------------------------------------------------------------------ *
 * What the API answers
 * ------------------------------------------------------------------ */

export type ReviewState = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface SignupPhone {
  type: string;
  number: string | null;
  display: string | null;
  dialCode: number | null;
}

export interface MatchSignup {
  id: number;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  emails: Array<string>;
  phones: Array<SignupPhone>;
  address1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  countryCode: string | null;
  externalId: string | null;
  isPossibleDuplicate: boolean;
  createdAt: string | null;
}

export interface MatchCandidate {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  phone: string | null;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  countryCode: string | null;
  createdAt: string | null;
  isDeleted: boolean;
  heldBy: number | null;
  approvable: boolean;
  contactId: number | null;
}

export interface MatchItem {
  nbSignupId: number;
  rule: number;
  review: ReviewState | 'AUTO';
  reason: string | null;
  userId: number | null;
  candidates: Array<MatchCandidate>;
  candidateCount: number;
  sameAccountAs: Array<number>;
  corroboratedBy: Array<string>;
  contactId: number | null;
  signup: MatchSignup | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  reviewedBy: { id: number; firstName: string | null; lastName: string | null } | null;
  decidedAt: string;
  /** Card 28: the merge the approval made, while it stands. */
  merge: MatchMerge | null;
  /** Card 28: approved before merging was built, its two contacts still apart. */
  mergeable: boolean;
}

export interface MergePart {
  key: string;
  label: string;
  changed: number;
}

export interface MatchMerge {
  id: number;
  fromContactId: number;
  intoContactId: number;
  mergedAt: string;
  mergedBy: { id: number; firstName: string | null; lastName: string | null } | null;
  results: Array<MergePart>;
}

export interface RuleCount {
  rule: number;
  count: number;
}

/* ------------------------------------------------------------------ *
 * The screen's state, all of it in the URL
 * ------------------------------------------------------------------ */

export type TabId = 'waiting' | 'approved' | 'rejected';

export interface MatchReviewState {
  tab: TabId;
  /** One rule's rows, or null for every rule. */
  rule: number | null;
  page: number;
  pageSize: PageSize;
}

export const EMPTY_REVIEW_STATE: MatchReviewState = { tab: 'waiting', rule: null, page: 0, pageSize: DEFAULT_PAGE_SIZE };

export const TABS: Array<{ id: TabId; review: ReviewState; label: string }> = [
  { id: 'waiting', review: 'PENDING', label: 'Waiting' },
  { id: 'approved', review: 'APPROVED', label: 'Approved' },
  { id: 'rejected', review: 'REJECTED', label: 'Rejected' },
];

export const reviewOfTab = (tab: TabId): ReviewState => TABS.find((t) => t.id === tab)?.review ?? 'PENDING';

/** The state a URL holds. Anything unreadable is left out, never guessed. */
export const parseReviewState = (params: URLSearchParams): MatchReviewState => {
  const tab = params.get('tab');
  const rule = Number(params.get('rule'));
  const page = Number(params.get('page'));
  const size = Number(params.get('size'));
  return {
    tab: tab === 'approved' || tab === 'rejected' ? tab : 'waiting',
    rule: Number.isInteger(rule) && rule >= 1 && rule <= 7 ? rule : null,
    page: Number.isInteger(page) && page > 0 ? page : 0,
    pageSize: (PAGE_SIZES as ReadonlyArray<number>).includes(size) ? (size as PageSize) : DEFAULT_PAGE_SIZE,
  };
};

/** The URL for a state: only what differs from the empty one. */
export const toReviewParams = (state: MatchReviewState): URLSearchParams => {
  const params = new URLSearchParams();
  if (state.tab !== 'waiting') params.set('tab', state.tab);
  if (state.rule) params.set('rule', String(state.rule));
  if (state.page) params.set('page', String(state.page));
  if (state.pageSize !== DEFAULT_PAGE_SIZE) params.set('size', String(state.pageSize));
  return params;
};

/** A change to what is shown starts again at the first page; a new tab shows every rule. */
export const withReviewChange = (state: MatchReviewState, change: Partial<MatchReviewState>): MatchReviewState => ({
  ...state,
  ...('tab' in change && change.tab !== state.tab ? { rule: null } : {}),
  ...change,
  page: 'page' in change ? (change.page as number) : 0,
});

/* ------------------------------------------------------------------ *
 * Words
 * ------------------------------------------------------------------ */

/** What each rule of the match ladder found (card 06), in a reviewer's words. */
export const RULES: Record<number, { label: string; hint: string }> = {
  1: { label: 'App id on the record', hint: "The NationBuilder record carries the app account's id (MobileApp-…)." },
  3: { label: "NationBuilder's own link", hint: "NationBuilder's user table points at this account." },
  4: { label: 'Same email', hint: 'An email on the record is the account’s.' },
  5: { label: 'Same phone', hint: 'A number on the record is the account’s.' },
  6: {
    label: 'Name and town',
    hint: 'Only the first name, last name, town and country agree. Namesakes are common: approve only on something else.',
  },
  7: { label: 'No account', hint: 'No rule found an account.' },
};

export const ruleLabel = (rule: number) => RULES[rule]?.label ?? `Rule ${rule}`;

/** Why the ladder left the row to a person. */
export const describeReason = (item: Pick<MatchItem, 'rule' | 'reason' | 'candidateCount'>): string => {
  if (item.reason === 'several accounts') return `${item.candidateCount.toLocaleString('en-US')} accounts fit: choose one, or reject`;
  if (item.reason === 'uncorroborated') return 'Nothing else agrees';
  if (item.rule === 6) return 'Name and town only';
  return item.reason ?? '';
};

const nameOf = (person: { firstName: string | null; lastName: string | null } | null | undefined) =>
  person ? [person.firstName, person.lastName].filter(Boolean).join(' ') : '';

export const signupName = (signup: MatchSignup | null, id: number) =>
  (signup && [signup.firstName, signup.middleName, signup.lastName].filter(Boolean).join(' ')) || `NationBuilder #${id}`;

export const candidateName = (c: MatchCandidate) => nameOf(c) || c.username || `Account #${c.userId}`;

export const reviewerName = (item: Pick<MatchItem, 'reviewedBy'>) => nameOf(item.reviewedBy) || 'an admin';

/** "Approved by ILBP Admin on 13 Oct 2026". */
export const describeDecision = (item: Pick<MatchItem, 'review' | 'reviewedAt' | 'reviewedBy'>) => {
  if (item.review !== 'APPROVED' && item.review !== 'REJECTED') return '';
  const when = item.reviewedAt ? ` on ${dayjs(item.reviewedAt).format('D MMM YYYY')}` : '';
  return `${item.review === 'APPROVED' ? 'Approved' : 'Rejected'} by ${reviewerName(item)}${when}`;
};

/** "Merged into contact #208090: details and consent 3, email addresses 1, tags 2." */
export const describeMerge = (merge: MatchMerge): string => {
  const parts = merge.results
    .filter((p) => p.key !== 'left')
    .map((p) => `${p.label.toLowerCase()} ${p.changed.toLocaleString('en-US')}`);
  const left = merge.results.find((p) => p.key === 'left');
  return (
    `One contact: merged into #${merge.intoContactId}` +
    (parts.length ? `, with ${parts.join(', ')}` : '') +
    (left ? `. ${left.changed.toLocaleString('en-US')} kept on the NationBuilder record, the member having their own` : '') +
    '.'
  );
};

/** A phone as NationBuilder stored it: both columns, and the dialling code beside them. */
export const describeSignupPhone = (p: SignupPhone) => {
  const shown = [p.number, p.display && p.display !== p.number ? p.display : null].filter(Boolean).join(' / ');
  return `${shown}${p.dialCode != null ? ` (code ${p.dialCode})` : ''}`;
};

export const PHONE_TYPES: Record<string, string> = { MOBILE: 'Mobile', HOME: 'Home', WORK: 'Work' };

/* ------------------------------------------------------------------ *
 * Which values agree
 * ------------------------------------------------------------------ */

const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Whether two values say the same, ignoring case and spacing. Only for names,
 * emails, towns and countries. Phones are never compared here: the one
 * definition of "the same phone" is the ladder's (crm-normalise.sql), and a
 * second one in the panel would drift from it.
 */
export const same = (a: string | null | undefined, b: string | null | undefined) => norm(a) !== '' && norm(a) === norm(b);

export interface Agreement {
  name: boolean;
  email: boolean;
  street: boolean;
  town: boolean;
  country: boolean;
  appId: boolean;
}

export const agreementOf = (signup: MatchSignup | null, c: MatchCandidate): Agreement => ({
  name: Boolean(signup) && same(signup?.firstName, c.firstName) && same(signup?.lastName, c.lastName),
  email: Boolean(signup?.emails.some((e) => same(e, c.email))),
  street: same(signup?.address1, c.streetAddress),
  town: same(signup?.city, c.city),
  country: same(signup?.countryCode, c.countryCode),
  appId: same(signup?.externalId, `MobileApp-${c.userId}`),
});

/** Why a candidate cannot be approved now, or null when it can. */
export const candidateBlock = (c: MatchCandidate): string | null => {
  if (c.isDeleted) return 'Deleted or banned: never linked';
  if (c.heldBy != null) return `Already linked to NationBuilder #${c.heldBy}`;
  if (!c.approvable) return 'Cannot be approved now';
  return null;
};

/* ------------------------------------------------------------------ *
 * A review (setNbMatchReview)
 * ------------------------------------------------------------------ */

/** The API's limit on a review note (nb-match-review.service.ts REVIEW_NOTE_MAX). */
export const NOTE_MAX = 1000;

/** A rule-6 approval says what agrees besides name and town (card 07); the API refuses one without. */
export const noteRequired = (item: Pick<MatchItem, 'rule'>, review: ReviewState) => review === 'APPROVED' && item.rule === 6;

export const isSeveralAccounts = (item: Pick<MatchItem, 'reason'>) => item.reason === 'several accounts';

/** The account an approval links: the row's own, or the one chosen among several. */
export const approvalAccount = (item: Pick<MatchItem, 'reason' | 'userId'>, chosen: number | null) =>
  isSeveralAccounts(item) ? chosen : item.userId;

/** What is wrong with an approval or rejection before it is sent, or null. */
export const checkReview = (
  item: Pick<MatchItem, 'rule' | 'reason' | 'userId'>,
  review: ReviewState,
  note: string,
  chosen: number | null,
): string | null => {
  if (note.trim().length > NOTE_MAX) return `A note is at most ${NOTE_MAX.toLocaleString('en-US')} characters.`;
  if (review === 'APPROVED' && approvalAccount(item, chosen) == null) return 'Choose the account this record is.';
  if (noteRequired(item, review) && !note.trim()) return 'Say what agrees besides the name and the town.';
  return null;
};

/** What a refused review means, in the dialog's words. */
export const describeReviewError = (error: ApolloError | Error): string => {
  const rejection = getRateLimitRejection(error);
  if (rejection) return describeRateLimitRejection(rejection);
  const graphQL = (error as ApolloError).graphQLErrors?.[0];
  switch (graphQL?.extensions?.code) {
    case 'NB_MATCH_ACCOUNT_TAKEN':
      return 'Another NationBuilder record is already linked to that account. Approve one record per account.';
    case 'NB_MATCH_NOTE_REQUIRED':
      return 'Say what agrees besides the name and the town.';
    case 'NB_MATCH_ACCOUNT_REQUIRED':
      return 'Choose the account this record is.';
    case 'NB_MATCH_MERGE_REFUSED':
      // Nothing was saved, the decision included: the API says why in its own words.
      return `${graphQL.message ?? 'The two contacts could not be merged.'} The decision was not saved.`;
    case 'NB_MATCH_NOT_APPROVED':
    case 'NB_MATCH_INVALID_ACCOUNT':
    case 'NB_MATCH_NOT_FOUND':
    case 'NB_MATCH_NOT_REVIEWABLE':
    case 'CRM_UNAVAILABLE':
    case 'BAD_REQUEST':
      return graphQL?.message ?? 'The review was refused.';
    default:
      return 'The review could not be saved. Try again.';
  }
};

/**
 * What a decision or a merge makes stale: the queue, its counts and the sidebar's count, and,
 * since card 28, the contacts it joined or separated, wherever they are listed or searched.
 */
export const evictAfterDecision = (cache: ApolloCache<unknown>) => {
  ['nbMatchReviewQueue', 'crmContacts', 'crmContact', 'crmContactCounts', 'crmContactSearch', 'crmContactTimeline'].forEach((fieldName) =>
    cache.evict({ id: 'ROOT_QUERY', fieldName }),
  );
  cache.gc();
};

/** What a refused page means. */
export const describeQueueError = (error: ApolloError): string => {
  const rejection = getRateLimitRejection(error);
  if (rejection) return describeRateLimitRejection(rejection);
  const graphQL = error.graphQLErrors?.[0];
  if (graphQL?.extensions?.code === 'CRM_UNAVAILABLE') {
    // Either the archive is not in this database, or the match ladder holds the table for its run.
    return /not in this database/.test(graphQL.message)
      ? `${graphQL.message} Match review reads the NationBuilder archive, which only the live CRM database holds.`
      : graphQL.message;
  }
  return 'The match review queue could not be loaded. Try again in a moment.';
};
